import React, { useState, useEffect } from 'react';
import { useCart } from '../context/CartContext';
import { useTheme } from '../context/ThemeContext';
import { Link, useNavigate } from 'react-router-dom';
import api from '../services/api';

const CartPage = () => {
    const { cartItems, updateQuantity, removeFromCart, cartTotals, loading } = useCart();
    const { theme } = useTheme();
    const navigate = useNavigate();
    const [orderNote, setOrderNote] = useState('');
    const [recentlyViewed, setRecentlyViewed] = useState([]);
    const [freeShippingThreshold, setFreeShippingThreshold] = useState(3500);
    const [continueShoppingLink, setContinueShoppingLink] = useState('/category/new-in');

    const primaryColor = theme.primaryColor;
    const secondaryColor = theme.secondaryColor;
    const textColor = theme.textColor;
    const borderColor = theme.borderColor || '#e5e5e5';
    const backgroundColor = theme.backgroundColor || '#ffffff';
    const mutedTextColor = theme.mutedText || '#888888';

    useEffect(() => {
        const fetchSettings = async () => {
            try {
                const res = await api.get('/settings/announcement');
                if (res.freeShippingThreshold) {
                    setFreeShippingThreshold(res.freeShippingThreshold);
                }
                if (res.continueShoppingLink) {
                    setContinueShoppingLink(res.continueShoppingLink);
                }
            } catch (err) {
                console.error('Failed to fetch settings');
            }
        };
        fetchSettings();
    }, []);

    useEffect(() => {
        const fetchRecentlyViewed = async () => {
            try {
                const res = await api.get('/products');
                const allProducts = Array.isArray(res) ? res : (res.products || []);
                const shuffled = allProducts.sort(() => 0.5 - Math.random());
                setRecentlyViewed(shuffled.slice(0, 4));
            } catch (err) {
                console.error('Failed to fetch products for Recently Viewed:', err);
            }
        };
        fetchRecentlyViewed();
    }, []);

    const subtotal = cartTotals?.subtotal || 0;
    const amountLeft = Math.max(0, freeShippingThreshold - subtotal);
    const progressPercentage = Math.min((subtotal / freeShippingThreshold) * 100, 100);

    if (loading && cartItems.length === 0) return <p className="text-center mt-20" style={{ color: textColor }}>Loading cart...</p>;

    return (
        <div className="bg-white min-h-screen" style={{ backgroundColor, color: textColor }}>
            <div className="max-w-[1440px] mx-auto px-4 md:px-8 lg:px-16 py-8 lg:py-12">
                <div className="text-center mb-8">
                    <h1 className="text-3xl font-light uppercase tracking-widest mb-2" style={{ color: textColor }}>CART</h1>
                    <Link to={continueShoppingLink} className="text-sm underline underline-offset-4 hover:opacity-70" style={{ color: mutedTextColor }}>
                        Continue shopping
                    </Link>
                </div>
                <div className="mb-8">
                    <div className="text-center text-sm mb-2" style={{ color: textColor }}>
                        You're only <span className="font-semibold" style={{ color: primaryColor }}>Rs. {amountLeft.toLocaleString()}</span> away from free shipping!
                    </div>
                    <div className="w-full h-1.5 rounded-full overflow-hidden" style={{ backgroundColor: borderColor }}>
                        <div
                            className="h-full transition-all duration-300"
                            style={{ width: `${progressPercentage}%`, backgroundColor: primaryColor }}
                        />
                    </div>
                </div>
                {cartItems.length === 0 ? (
                    <div className="empty-cart text-center py-16 min-h-[40vh] flex flex-col items-center justify-center" style={{ backgroundColor }}>
                        <p className="text-lg mb-6" style={{ color: textColor }}>Your cart is currently empty.</p>
                        <Link to={continueShoppingLink} className="text-white px-8 py-3 uppercase tracking-widest text-sm font-semibold rounded-sm hover:opacity-85 transition-opacity" style={{ backgroundColor: primaryColor }}>
                            Continue Shopping
                        </Link>
                    </div>
                ) : (
                    <div className="flex flex-col lg:flex-row gap-10 lg:gap-16">
                        <div className="flex-1 w-full lg:w-[65%]">
                            {cartItems.map((item, index) => (
                                <div key={item.id} className={`flex gap-6 py-6 ${index !== 0 ? 'border-t' : ''} relative`} style={{ borderColor }}>
                                    {loading && <div className="absolute inset-0 bg-white/50 z-10" />}
                                    <Link to={`/product/${item.productId}`} className="w-[90px] sm:w-[110px] md:w-[120px] flex-shrink-0">
                                        <img src={item.image} alt={item.name} className="w-full aspect-[3/4] object-cover border" style={{ borderColor }} />
                                    </Link>
                                    <div className="flex-1 flex flex-col justify-between py-1">
                                        <div className="flex justify-between items-start gap-4">
                                            <div>
                                                <Link to={`/product/${item.productId}`} className="text-[15px] sm:text-[17px] transition-colors mb-2 block hover:opacity-70" style={{ color: textColor }}>
                                                    {item.name}
                                                </Link>
                                                {item.options?.color && (
                                                    <p className="text-[13.5px] mb-1" style={{ color: mutedTextColor }}>
                                                        <span className="font-semibold" style={{ color: textColor }}>Color:</span> {item.options.color}
                                                    </p>
                                                )}
                                                {item.options?.size && (
                                                    <p className="text-[13.5px]" style={{ color: mutedTextColor }}>
                                                        <span className="font-semibold" style={{ color: textColor }}>Size:</span> {item.options.size}
                                                    </p>
                                                )}
                                            </div>
                                            <div className="text-[15px] sm:text-[16px] whitespace-nowrap pt-1" style={{ color: textColor }}>
                                                Rs. {item.price.toLocaleString()}
                                            </div>
                                        </div>
                                        <div className="mt-4">
                                            <div className="flex items-center border w-fit h-[36px]" style={{ borderColor }}>
                                                <button
                                                    onClick={() => updateQuantity(item.id, item.quantity - 1)}
                                                    disabled={loading}
                                                    className="w-[36px] h-full flex items-center justify-center text-xl transition-colors hover:bg-opacity-10" style={{ color: textColor }}
                                                >
                                                    −
                                                </button>
                                                <span className="w-[44px] text-center text-[15px] font-medium border-x h-full flex items-center justify-center" style={{ borderColor, color: textColor }}>
                                                    {item.quantity}
                                                </span>
                                                <button
                                                    onClick={() => updateQuantity(item.id, item.quantity + 1)}
                                                    disabled={loading}
                                                    className="w-[36px] h-full flex items-center justify-center text-xl transition-colors hover:bg-opacity-10" style={{ color: textColor }}
                                                >
                                                    +
                                                </button>
                                            </div>
                                            <button
                                                onClick={() => removeFromCart(item.id)}
                                                disabled={loading}
                                                className="mt-3 text-[14px] hover:underline underline-offset-4 transition-all" style={{ color: mutedTextColor }}
                                            >
                                                Remove
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                        <div className="w-full lg:w-[35%] xl:w-[32%]">
                            <div className="p-6 lg:p-8 rounded-sm lg:sticky lg:top-24 h-fit max-h-[calc(100vh-7rem)] overflow-y-auto" style={{ backgroundColor: borderColor }}>
                                <label className="block text-[12px] tracking-[2.5px] uppercase mb-4" style={{ color: textColor }}>
                                    Order Note
                                </label>
                                <textarea
                                    value={orderNote}
                                    onChange={(e) => setOrderNote(e.target.value)}
                                    className="w-full border p-3 text-[14px] outline-none min-h-[110px] resize-y mb-6 focus:ring-1" style={{ borderColor, backgroundColor, color: textColor }}
                                ></textarea>
                                <div className="flex justify-between items-center mb-4">
                                    <span className="text-[16px]" style={{ color: textColor }}>Subtotal</span>
                                    <span className="text-[16px]" style={{ color: textColor }}>Rs. {subtotal.toLocaleString()}</span>
                                </div>
                                <button
                                    onClick={() => navigate('/checkout')}
                                    className="w-full text-white py-3 text-[14px] font-semibold uppercase transition-colors rounded-sm hover:opacity-85"
                                    style={{ backgroundColor: primaryColor }}
                                >
                                    Check out
                                </button>
                                <p className="text-[13px] text-center mt-4 leading-relaxed" style={{ color: mutedTextColor }}>
                                    Shipping, taxes, and discount codes calculated at checkout.
                                </p>
                            </div>
                        </div>
                    </div>
                )}
                <div className="text-center mt-16 mb-8">
                    <h2 className="text-xl font-light uppercase tracking-widest mb-2" style={{ color: textColor }}>EXPECTED DELIVERY</h2>
                    <p className="text-sm" style={{ color: mutedTextColor }}>Your parcel will be delivered in 3-7 Working Days.</p>
                </div>
                <div className="mt-16 mb-8">
                    <h2 className="text-xl font-light uppercase tracking-widest text-center mb-8" style={{ color: textColor }}>RECENTLY VIEWED</h2>
                    {recentlyViewed.length === 0 ? (
                        <p className="text-center text-sm" style={{ color: mutedTextColor }}>No recently viewed products.</p>
                    ) : (
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-6">
                            {recentlyViewed.map((product) => (
                                <Link key={product._id} to={`/product/${product._id}`} className="group">
                                    <div className="aspect-[3/4] overflow-hidden bg-gray-100 mb-3">
                                        <img
                                            src={product.images?.[0] || 'https://via.placeholder.com/400'}
                                            alt={product.name}
                                            className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                                        />
                                    </div>
                                    <h3 className="text-[11px] font-medium uppercase tracking-widest mb-1 truncate" style={{ color: textColor }}>
                                        {product.name}
                                    </h3>
                                    <p className="text-sm font-medium" style={{ color: primaryColor }}>Rs. {product.price.toLocaleString()}</p>
                                </Link>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default CartPage;